import React, { useEffect, useMemo, useState, useRef } from 'react';
import { DatePicker } from 'tea-component';
const { QuarterPicker } = DatePicker;
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { handleDetailDemographicsFilterParamsToApi } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import DemographicsContext from './index';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import {
  getDetailCommonValidUrlParams,
  detailDemographicsValidCfg,
  hasShortLinkRatio,
} from '@intelligence/_config/urlPageValidCfg';
import { useShortLink } from '@intelligence/hooks/useShortLink';
import { useSelector } from 'react-redux';
import AppstoreIcon from '@/pages/intelligence/assets/svg/dataSource/appstore.svg';
import GoogleplayIcon from '@/pages/intelligence/assets/svg/dataSource/googleplay.svg';
import IconDom from '@/pages/intelligence/components/SelectCompoenet/ItemDom/IconDom';
import useDemographicsRegionsOptions from '@/pages/intelligence/hooks/useDemographicsRegionsOptions';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { addGlobalRegion } from '@intelligence/v3/utils/region';
import { SOURCE_LIST } from './_config/constant';
import { getGameBasicinfoNew } from '@intelligence/services/topcharts';


function isHasUsCountry(list: any) {
  if (!Array.isArray(list)) return '';
  if (list.length > 1) {
    if (list.findIndex((n) => n?.toLocaleLowerCase() === 'us') > -1) {
      return 'us';
    }
    return list[0];
  }
  return '';
}

const iconName = {
  ios: AppstoreIcon,
  android: GoogleplayIcon,
} as any;

export default function DemographicsCom(props: any) {
  const { appIdList: appIdListAll, demographicsIdsList, urlFilterValue, gameId } = props;
  // 当前数据源（用于控制国家筛选器是否展示 global）
  const [activeDatasource, setActiveDatasource] = useState(SOURCE_LIST.SENSORTOWER);
  const [appIdList, setAppIdList] = useState([]);
  // 切换数据源执行函数：先请求 topcharts 拿到 app_id/app_ids，供后续 metrics 请求使用
  const handleActiveDatasourceChange = async (source: string, markets: string[] ) => {
    setActiveDatasource(source);
    const gameBasicinfo = await getGameBasicinfoNew({ source, filter_ids: [gameId], markets });
    const first = gameBasicinfo?.data?.rank_list?.[0] || {};
    const { app_id, app_ids } = first;
    const appIdsSet = new Set((app_ids || []).map((id: string | number) => String(id)));
    const filteredList = appIdListAll.filter((item: any) => appIdsSet.has(String(item.id)));
    setAppIdList(filteredList);
    return { app_id, app_ids };
  };
  const [countryFilterParams, setCountryFilterParams] = useState<{
    app_ids: string[];
    source?: string;
    granularity?: string;
  } | null>(null);
  const lastFilterKeyRef = useRef('');
  // 切换筛选器执行函数 打印筛选器内容
  const handleFilterChange = (filter: any) => {
    const appIdValue = filter?.[filterKeyMap.APP_SELECTOR]?.value;
    const app_ids = Array.isArray(appIdValue) ? appIdValue : [appIdValue].filter(Boolean);
    const sourceRaw = filter?.[filterKeyMap.DATASOURCE]?.value;
    const source = Array.isArray(sourceRaw) ? sourceRaw?.[0] : sourceRaw;
    const rangeTime = filter?.[filterKeyMap.RANGE_TIME];
    const rawGranularity = rangeTime?.type || rangeTime?.granularity || 'lifetime';
    const GRANULARITY_MAP = {
      month: 'monthly',
      quarter: 'quarterly',
    } as any;
    const granularity = GRANULARITY_MAP[rawGranularity] || rawGranularity;
    const nextKey = `${source || ''}|${granularity || ''}|${app_ids.join(',')}`;
    if (nextKey === lastFilterKeyRef.current) return;
    lastFilterKeyRef.current = nextKey;
    setCountryFilterParams({ app_ids, source, granularity });
  };

  const {
    regionOptions: regionOptionsRaw,
    allMarkets,
    marketLoading: filterLoading,
  } = useDemographicsRegionsOptions({
    appIds: countryFilterParams?.app_ids || [],
    source: countryFilterParams?.source,
    granularity: countryFilterParams?.granularity,
    waitForParams: true,
  });
  const regionOptionsNoGlobal = useMemo<any[]>(() => {
    if (!Array.isArray(regionOptionsRaw)) return regionOptionsRaw as any[];
    return (regionOptionsRaw as any[]).filter((item: any) => {
      if (typeof item === 'string') return item?.toLowerCase?.() !== 'global';
      return item?.area_code !== 'global' && item?.value?.toLowerCase?.() !== 'global';
    });
  }, [regionOptionsRaw]);

  const regionOptions = useMemo(() => {
    return activeDatasource === SOURCE_LIST.SENSORTOWER
      ? addGlobalRegion(regionOptionsRaw)
      : regionOptionsNoGlobal;
  }, [activeDatasource, regionOptionsNoGlobal, regionOptionsRaw]);


  // 是否有短链接更新功能
  const hasUrlShortLink = hasShortLinkRatio.detailMobileDemographics;
  const {
    initShortLinkCfgNoAwait,
    shortLinkLoading,
    urlCustomParams,
    shortLinkFilterValue,
    shortLinkValidQuery,
  } = useShortLink({
    urlFilterValue,
    hasUrlShortLink,
    getPageValidCfg: () => detailDemographicsValidCfg,
    getCustomValidUrlParams: getDetailCommonValidUrlParams,
  });

  useEffect(() => {
    // 进入页面时判断是否有tokenQuery等，有则进入
    initShortLinkCfgNoAwait();
  }, []);

  const getOptions = (appIdList: any[]) => {
    const opts = appIdList?.map((item) => ({
      value: item?.id,
      text: (
        <IconDom
          data={{
            value: item?.id,
            text: item?.name,
            img: item?.cover,
            svg: iconName[item?.platform],
          }}
        />
      ),
    }));
    return opts;
  };

  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const { demographicsAppid } = topchartsState;
  // 所有app
  const appIds = appIdList.map((item: any) => item?.id);
  // 默认选中的app，有数据的app优先
  const defaultAppId = demographicsIdsList?.length ? demographicsIdsList[0] : appIds?.[0];
  // 初始化选中的app，若继承的app合法（即所有app中包含继承过来的app），则用继承的app左右初始化选中的app，否则用默认选中的app 作为初始化选中的app
  const activeAppid = appIds?.includes(demographicsAppid) ? demographicsAppid : defaultAppId;

  const defaultFilterValue = useMemo(
    () => ({
      [filterKeyMap.RANGE_TIME]: {
        // data.ai 维持 Month-range；Sensor Tower 用 Lifetime/Quarter 下拉（默认 Lifetime）
        type: activeDatasource === SOURCE_LIST.APPANNIE ? 'month' : 'lifetime',
        value: ['', ''],
      },
      [filterKeyMap.COMPOSED_COUNTRY_SELECT]: {
        value: {
          type: 'single',
          countries: activeDatasource === SOURCE_LIST.SENSORTOWER ? ['global'] : ['us'],
        },
        overlayClassName: 'dtb-demographics-country-overlay',
      },
      [filterKeyMap.APP_SELECTOR]: { value: activeAppid },
      [filterKeyMap.DATASOURCE]: { value: activeDatasource },
    }),
    [activeAppid, activeDatasource],
  );


  const filterCfg: TopchartFilterCfgType = useMemo(
    () => ({
      filterKeyList: [
        filterKeyMap.DATASOURCE,
        filterKeyMap.APP_SELECTOR,
        filterKeyMap.COMPOSED_COUNTRY_SELECT,
        filterKeyMap.RANGE_TIME,
      ],
      regionFilterParams: {},
      defaultValue: defaultFilterValue,
      resetFilterValue: {
        ...defaultFilterValue,
      },
      specialParams: {
        regionOptions, // 国家筛选器options,
        defalutMarket:
          activeDatasource === SOURCE_LIST.SENSORTOWER
            ? ['global']
            : [isHasUsCountry(allMarkets) || 'us'], // 没取到交集用的默认值
        disableNoChina: true,
        disableGlobal: activeDatasource !== SOURCE_LIST.SENSORTOWER,

        // 时间筛选器：data.ai 维持 Month-range（无下拉 + 可输入）；Sensor Tower 用默认样式 + Lifetime/Quarter 下拉
        rangeTimeType:
          activeDatasource === SOURCE_LIST.APPANNIE ? FilterTimeType.rangeMonth : FilterTimeType.commonRange,
        showSelectType: activeDatasource !== SOURCE_LIST.APPANNIE,
        showShortcut: false,
        typeList: activeDatasource === SOURCE_LIST.APPANNIE ? ['month'] : ['lifetime', 'quarter'],
        [filterKeyMap.RANGE_TIME]: {
          // 注意：RANGE_TIME 自身也需要显式声明 showSelectType/typeList，避免切源后沿用旧 inputList 配置
          showSelectType: activeDatasource !== SOURCE_LIST.APPANNIE,
          typeList: activeDatasource === SOURCE_LIST.APPANNIE ? ['month'] : ['lifetime', 'quarter'],

          // Sensor Tower：Lifetime 禁用输入（固定范围展示）；Quarter 允许选择且只选一个季度
          selectClassName: 'dbi_demographics-select',
          customComponentClassName: 'dbi_demographics-custompicker',
          rangePickerClassName: 'dbi_demographics-rangepicker',
          overlayClassName: 'dbi_demographics-rangepicker-overlay',
          optionsMap: {
            lifetime: { value: 'lifetime', text: 'Lifetime', text_cn: '全周期', type: 'date' },
          },
          customOption: {
            lifetime: {
              disabled: activeDatasource === SOURCE_LIST.SENSORTOWER,
            },
            quarter: {
              showSelectType: false,
              isSingleSelect: true,
              customRender: ({ value, onChange, disabled, className, ...rest }: any) => {
                const pickerValue = Array.isArray(value) ? (value[0] && value[0] !== '' ? value[0] : undefined) : (value || undefined);
                return (
                  <QuarterPicker
                    className={className}
                    value={pickerValue}
                    onChange={(val: any) => {
                      if (!val) return onChange([null, null]);
                      const startOfQuarter = val.clone().startOf('quarter');
                      onChange([startOfQuarter, startOfQuarter.clone()]);
                    }}
                    disabled={disabled}
                    {...rest}
                  />
                );
              },
            },
          },
        },


        isSimpleCountry: true,
        sourceFilterSingleType: 'single',
        [filterKeyMap.DATASOURCE]: {
          isShowButtonLabel: true, // 显示标签
        },
        [filterKeyMap.APP_SELECTOR]: {
          options: getOptions(appIdList),
          value: activeAppid,
        },
      },

      showConfirmButton: false,
      haveFilterSaveBtn: false,
      handleFilterToApiParams: handleDetailDemographicsFilterParamsToApi,
      hasUrlShortLink,
    }),
    [
      allMarkets,
      hasUrlShortLink,
      activeAppid,
      appIdList,
      defaultFilterValue,
      regionOptions,
      activeDatasource,
    ],
  );


  return (
    <DemographicsContext
      {...props}
      appIdList={appIdList}
      filterLoading={filterLoading || shortLinkLoading}
      // 筛选器相关配置如筛选器展示顺序，默认值等
      filterCfg={filterCfg}
      onDatasourceChange={handleActiveDatasourceChange}
      // 从短链接中拿到校正后的url参数（与页面相关）
      urlCustomParams={urlCustomParams}
      // 筛选器有效的页面配置值
      shortLinkValidQuery={shortLinkValidQuery}
      // 从短链接拿到的筛选器分享值
      shortLinkFilterValue={shortLinkFilterValue}
      allMarkets={allMarkets}
      activeAppid={activeAppid}
      onFilterChange={handleFilterChange}
    />
  );
}

